import { Card, Modal, Panel } from '@/components/Bicester';
import { ROLE_TYPE } from '@/constants';
import useAccountBasic from '@/hooks/preload/useAccountBasic';
import useRouter from '@/hooks/useRouter';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { fetchUpgradeLevel, fetchUpgradeYishang } from '@/services/account';
import { SERVICE_STATUS } from '@/services/constants';
import { useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Alert, Form } from 'rsuite';
import { ItemDataType } from 'rsuite5/esm/@types/common';
import * as Yup from 'yup';

interface UpgradeLevelForm {
  valid_level_time: string; // 等级到期日
  level: string | number; // 调整后的等级
  content: string; // 调整原因
  remark: string; // 身份备注或详情
  approver: string; // 审批人
}

interface UpgradeYishangForm {
  pcs_id: string | number; // 匹配大使id
  valid_day: string; // 有效期 3个月,6个月,9个月,明年年底
}

const MbrPromotion = ({
  isEdit,
  data,
  saList,
  onReGetInfo,
}: {
  isEdit: boolean;
  data: any;
  saList: ItemDataType[];
  onReGetInfo?: () => void; // 重新获取用户info
}) => {
  const { match, query } = useRouter<{ id: string }>();
  const { profile } = useReadyInfo();
  const { data: accountBasic, loading: accountBasicLoading } = useAccountBasic();

  const defaultUpgradeLevel: UpgradeLevelForm = {
    valid_level_time: '',
    level: null,
    content: '',
    remark: '',
    approver: '',
  };
  const defaultYishangInfo: UpgradeYishangForm = {
    pcs_id: '',
    valid_day: '',
  };

  const [defaultLevelInfo, setDefaultLevelInfo] = useState<UpgradeLevelForm>(defaultUpgradeLevel);
  const [saveInfoModal, setSaveInfoModal] = useState({
    show: false,
    content: '',
    confirm: '',
    cancel: '',
    onConfirm: null,
  });

  const { run: upgradeLevel, loading: upgradeLevelLoading } = useRequest(fetchUpgradeLevel, {
    manual: true,
  });

  const { run: upgradeYishang, loading: upgradeYishangLoading } = useRequest(fetchUpgradeYishang, {
    manual: true,
  });

  const levelFormik = useFormik({
    initialValues: defaultLevelInfo,
    enableReinitialize: true, // initialValues默认值受控
    validationSchema: Yup.object({
      valid_level_time: Yup.string().ensure().required('请选择等级到期日'),
      content: Yup.string().required('请选择等级调整原因'),
      remark: Yup.string().required('请填写身份备注或详情'),
      approver: Yup.string().required('请填写审批人'),
    }),
    onSubmit(values, actions) {
      if (levelFormik.isValid) {
        setSaveInfoModal({
          show: true,
          content: '是否调整等级？',
          confirm: '调整',
          cancel: '暂不',
          onConfirm: () => handleUpgradeLevel(_.pickBy(values)),
        });
      }
    },
  });

  const yishangFormik = useFormik({
    initialValues: defaultYishangInfo,
    enableReinitialize: true, // initialValues默认值受控
    validationSchema: Yup.object({
      pcs_id: Yup.number().required('请选择匹配大使'),
      valid_day: Yup.string().ensure().required('请选择有效期'),
    }),
    onSubmit(values, actions) {
      if (yishangFormik.isValid) {
        setSaveInfoModal({
          show: true,
          content: '是否发送邀请？',
          confirm: '发送',
          cancel: '暂不',
          onConfirm: () => handleUpgradeYishang(values),
        });
      }
    },
  });

  const handleUpgradeLevel = (values) => {
    upgradeLevel({
      data: {
        ...values,
        id: match.params.id,
      },
    }).then((res) => {
      if (res?.status === SERVICE_STATUS.SUCCESS) {
        levelFormik.resetForm();
        Alert.success(res?.data);
        onReGetInfo();
      }
    });
    handleHideModal();
  };

  const handleUpgradeYishang = (values) => {
    upgradeYishang({
      data: {
        ...values,
        id: match.params.id,
      },
    }).then((res) => {
      if (res?.member_elite_id) {
        yishangFormik.resetForm();
        Alert.success('邀请成功');
        onReGetInfo();
      }
    });
    handleHideModal();
  };

  useEffect(() => {
    if (data?.valid_level_time) {
      levelFormik.setFieldValue('valid_level_time', data?.valid_level_time);
      setDefaultLevelInfo({
        ...defaultLevelInfo,
        valid_level_time: data?.valid_level_time,
      });
    }
  }, [data]);

  useEffect(() => {
    if (!isEdit) {
      // 重置表单
      levelFormik.resetForm();
      yishangFormik.resetForm();
    }
  }, [isEdit]);

  const handleHideModal = () => {
    setSaveInfoModal({
      show: false,
      content: '',
      confirm: '',
      cancel: '',
      onConfirm: null,
    });
  };

  return (
    <>
      <Panel
        title="会员升降级"
        loading={accountBasicLoading || upgradeLevelLoading || upgradeYishangLoading}
      >
        <div className="flex">
          {[ROLE_TYPE.MANAGER, ROLE_TYPE.DRT].includes(profile.role) && ( // GE经理和DRT导购大使才有会员升降级
            <FormikProvider value={levelFormik}>
              <Form className="flex-1 mr-[77px]">
                <Card title="升降级">
                  <Card.Input
                    notEditable
                    label="当前会员等级"
                    formKey="current_level"
                    inputType="level"
                    value={data?.level}
                    labelWeight={1}
                    contentWeight={3}
                  />
                  <Card.DatePicker
                    isEdit={isEdit}
                    label="当前等级到期日"
                    formKey="valid_level_time"
                    cleanable={false}
                    labelWeight={1}
                    contentWeight={3}
                  />
                  <Card.Picker
                    isEdit={isEdit}
                    label="调整成为"
                    formKey="level"
                    pickerType="levelNotYishang"
                    disabledItemValues={[data?.level]}
                    labelWeight={1}
                    contentWeight={3}
                  />
                  <Card.Picker
                    required
                    isEdit={isEdit}
                    label="调整原因"
                    formKey="content"
                    pickerData={accountBasic?.adjust_course}
                    labelWeight={1}
                    contentWeight={3}
                  />
                  <Card.Input
                    required
                    isEdit={isEdit}
                    label="身份备注或详情"
                    formKey="remark"
                    labelWeight={1}
                    contentWeight={3}
                  />
                  <Card.Input
                    required
                    isEdit={isEdit}
                    label="审批人"
                    formKey="approver"
                    labelWeight={1}
                    contentWeight={3}
                    hasBtn
                    showBtn={isEdit}
                    btnLoading={upgradeLevelLoading}
                    btnLabel="确认调整级别"
                    onClickBtn={() => {
                      levelFormik.submitForm();
                    }}
                  />
                </Card>
              </Form>
            </FormikProvider>
          )}
          <FormikProvider value={yishangFormik}>
            <Form className="flex-1">
              <Card title="邀请成为奕尚">
                <Card.Picker
                  isEdit={isEdit}
                  label="匹配大使"
                  formKey="pcs_id"
                  pickerData={saList}
                  searchable
                  labelWeight={1}
                  contentWeight={3}
                />
                <Card.Picker
                  isEdit={isEdit}
                  label="邀请奕尚有效期"
                  formKey="valid_day"
                  pickerData={[
                    { label: '3个月', value: '3个月' },
                    { label: '6个月', value: '6个月' },
                    { label: '9个月', value: '9个月' },
                    { label: '明年年底', value: '明年年底' },
                  ]}
                  labelWeight={1}
                  contentWeight={3}
                  hasBtn
                  showBtn={isEdit}
                  btnLoading={upgradeYishangLoading}
                  btnLabel="发送奕尚邀请"
                  onClick={() => {
                    yishangFormik.submitForm();
                  }}
                />
              </Card>
            </Form>
          </FormikProvider>
        </div>
      </Panel>
      <Modal
        show={saveInfoModal.show}
        content={saveInfoModal.content}
        confirm={saveInfoModal.confirm}
        cancel={saveInfoModal.cancel}
        onConfirm={saveInfoModal.onConfirm}
        onCancel={handleHideModal}
      />
    </>
  );
};

export default MbrPromotion;
